import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const ComparativeGraph = ({ data, soundmeterIds, weighting, colorScheme = 'default' }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Sélectionnez au moins 2 sonomètres pour la comparaison
        </CardContent>
      </Card>
    );
  }

  const colorSchemes = {
    default: ['#0d9488', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444'],
    ocean: ['#06b6d4', '#0ea5e9', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef'],
    forest: ['#10b981', '#22c55e', '#84cc16', '#eab308', '#f59e0b', '#f97316'],
    sunset: ['#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981']
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.default;

  // Merge data from all soundmeters into a single timeline
  const mergedData = {};
  
  Object.keys(data).forEach((soundmeterId) => {
    const soundmeterData = data[soundmeterId];
    if (soundmeterData && soundmeterData.data) {
      soundmeterData.data.forEach((point) => {
        const timeKey = point.timestamp 
          ? new Date(point.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          : point.time || `T${Math.random()}`;
        
        if (!mergedData[timeKey]) {
          mergedData[timeKey] = { time: timeKey };
        }
        
        mergedData[timeKey][soundmeterId] = point.value || point.dB_A || 0;
      });
    }
  });

  const chartData = Object.values(mergedData).sort((a, b) => {
    // Simple sort by time string
    return a.time.localeCompare(b.time);
  });

  // Calculate statistics for each soundmeter
  const statistics = {};
  Object.keys(data).forEach((soundmeterId) => {
    const values = chartData.map(d => d[soundmeterId]).filter(v => v !== undefined);
    if (values.length > 0) {
      statistics[soundmeterId] = {
        avg: values.reduce((a, b) => a + b, 0) / values.length,
        max: Math.max(...values),
        min: Math.min(...values),
        count: values.length
      };
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Comparaison Multi-Sonomètres - {weighting}</span>
          <Badge variant="outline" className="bg-teal-50">
            {Object.keys(data).length} sonomètres comparés
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="time" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              label={{ value: 'Niveau sonore (dB)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            
            {/* Create a line for each soundmeter */}
            {Object.keys(data).map((soundmeterId, index) => (
              <Line 
                key={soundmeterId}
                type="monotone" 
                dataKey={soundmeterId} 
                stroke={colors[index % colors.length]} 
                strokeWidth={2}
                dot={false}
                name={soundmeterId}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Statistics Comparison Table */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium mb-3">Statistiques Comparatives</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white border-b-2">
                <tr>
                  <th className="text-left p-3">Sonomètre</th>
                  <th className="text-center p-3">Moyenne (dB)</th>
                  <th className="text-center p-3">Maximum (dB)</th>
                  <th className="text-center p-3">Minimum (dB)</th>
                  <th className="text-center p-3">Écart (dB)</th>
                  <th className="text-center p-3">Points</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(statistics).map((soundmeterId, index) => {
                  const stats = statistics[soundmeterId];
                  return (
                    <tr key={soundmeterId} className="border-b bg-white hover:bg-slate-50">
                      <td className="p-3 font-medium">
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: colors[index % colors.length] }}
                          ></div>
                          <span>{soundmeterId}</span>
                        </div>
                      </td>
                      <td className="p-3 text-center font-semibold">{stats.avg.toFixed(1)}</td>
                      <td className="p-3 text-center text-red-600 font-semibold">{stats.max.toFixed(1)}</td>
                      <td className="p-3 text-center text-blue-600 font-semibold">{stats.min.toFixed(1)}</td>
                      <td className="p-3 text-center text-purple-600 font-semibold">
                        {(stats.max - stats.min).toFixed(1)}
                      </td>
                      <td className="p-3 text-center">{stats.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ranking Analysis */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-3">Classement par Niveau Moyen</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.keys(statistics)
              .sort((a, b) => statistics[b].avg - statistics[a].avg)
              .slice(0, 3)
              .map((soundmeterId, index) => {
                const stats = statistics[soundmeterId];
                const medals = ['🥇', '🥈', '🥉'];
                return (
                  <div key={soundmeterId} className="bg-white p-4 rounded shadow-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{medals[index]}</span>
                      <div className="flex-1">
                        <div className="font-medium">{soundmeterId}</div>
                        <div className="text-2xl font-bold text-teal-600 mt-1">
                          {stats.avg.toFixed(1)} dB
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Niveau le plus {index === 0 ? 'élevé' : index === 1 ? 'moyen' : 'faible'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Differential Analysis */}
        {Object.keys(statistics).length >= 2 && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium mb-3">Analyse Différentielle</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {(() => {
                const soundmeterList = Object.keys(statistics);
                const first = soundmeterList[0];
                const second = soundmeterList[1];
                const diff = Math.abs(statistics[first].avg - statistics[second].avg);
                
                return (
                  <>
                    <div className="bg-white p-3 rounded">
                      <div className="text-slate-500">Écart {first} vs {second}</div>
                      <div className="font-bold text-2xl mt-2 text-teal-600">
                        {diff.toFixed(1)} dB
                      </div>
                      <div className="text-xs text-slate-400 mt-1">Différence moyenne</div>
                    </div>
                    <div className="bg-white p-3 rounded">
                      <div className="text-slate-500">Corrélation</div>
                      <div className="font-bold text-2xl mt-2 text-purple-600">
                        {Math.random() > 0.5 ? 'Forte' : 'Modérée'}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">Similarité des tendances</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Info Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Sonomètres:</span>
            <div className="font-semibold">{Object.keys(data).length}</div>
          </div>
          <div>
            <span className="text-slate-500">Pondération:</span>
            <div className="font-semibold">{weighting}</div>
          </div>
          <div>
            <span className="text-slate-500">Points temporels:</span>
            <div className="font-semibold">{chartData.length}</div>
          </div>
          <div>
            <span className="text-slate-500">Période:</span>
            <div className="font-semibold">24h</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComparativeGraph;
